import { Loader, Pause, Play, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { cn } from '@/lib/utils';

import { StopButton } from './stop-button';

/**
 * CompactView - Minimalist vertical timer layout for collapsed sidebar.
 *
 * Displays only control buttons (play/stop, add, reset) and a small timer.
 * Uses Tooltips to show hints since there's no space for text labels.
 * Ideal for narrow panels and mobile devices.
 */
interface CompactViewProps {
  displayTime: string;
  className?: string;
  isActive: boolean;
  isPaused: boolean;
  isLimitReached: boolean;

  // Loading state per action.
  isStartPending?: boolean;
  isTogglePausePending?: boolean;
  isStopPending?: boolean;
  isDiscardPending?: boolean;
  isAnyActionPending?: boolean;

  // Handlers
  openCreateModal: () => void;
  openProjectSelector: () => void;
  toggle: () => void;
  togglePause: () => void;
  reset: () => void;
}

export function CompactView({
  displayTime,
  className,
  isActive,
  isPaused,
  isLimitReached,
  isStartPending,
  isTogglePausePending,
  isStopPending,
  isDiscardPending,
  isAnyActionPending,
  openCreateModal,
  toggle,
  togglePause,
  reset,
}: CompactViewProps) {
  const { t } = useTranslation(['authenticated.timeTracking', 'common']);

  // State-colored dot lets the user see the tracker is live without
  // expanding the sidebar.
  const statusDotClass = isLimitReached ? 'bg-destructive' : isPaused ? 'bg-warning' : 'bg-success';

  return (
    <div className={cn('flex flex-col items-center gap-3 px-1', className)}>
      {/* Live status surfacing: state dot + elapsed time while a timer runs */}
      {isActive && (
        <div className="flex flex-col items-center gap-1">
          <div className={cn('h-1.5 w-1.5 rounded-full', statusDotClass)} />
          <div
            className={cn(
              'text-[9px] font-medium tabular-nums',
              isPaused && !isLimitReached && 'text-muted-foreground'
            )}
          >
            {displayTime}
          </div>
        </div>
      )}

      {isActive && !isLimitReached && (
        <SimpleTooltip side="right" content={isPaused ? t('tracker.resume') : t('tracker.pause')}>
          <Button
            variant="default"
            size="icon"
            className="h-10 w-10 rounded-full"
            onClick={togglePause}
            disabled={isAnyActionPending}
          >
            {isTogglePausePending ? (
              <Loader className="h-5 w-5 animate-spin" />
            ) : isPaused ? (
              <Play fill="currentColor" className="h-5 w-5" />
            ) : (
              <Pause fill="currentColor" className="h-5 w-5" />
            )}
          </Button>
        </SimpleTooltip>
      )}

      {isActive && (
        <SimpleTooltip side="right" content={t('tracker.stop')}>
          <StopButton
            buttonClassName="h-8 w-8 rounded-full"
            toggle={toggle}
            isLoading={isStopPending}
            disabled={isAnyActionPending}
          />
        </SimpleTooltip>
      )}

      {!isActive && (
        <SimpleTooltip side="right" content={t('tracker.start')}>
          <Button
            variant="default"
            size="icon"
            className="h-10 w-10 rounded-full"
            onClick={toggle}
            disabled={isStartPending}
          >
            {isStartPending ? (
              <Loader className="h-5 w-5 animate-spin" />
            ) : (
              <Play fill="currentColor" className="h-5 w-5" />
            )}
          </Button>
        </SimpleTooltip>
      )}

      {!isActive && (
        <SimpleTooltip side="right" content={t('tracker.addEntry')}>
          <Button
            variant="secondary"
            size="icon"
            className="h-8 w-8 rounded-full bg-muted-foreground/15 hover:bg-muted-foreground/25"
            onClick={openCreateModal}
            disabled={isStartPending}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </SimpleTooltip>
      )}

      {isActive && (
        <SimpleTooltip side="right" content={t('tracker.reset')}>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full text-muted-foreground hover:text-destructive"
            onClick={reset}
            disabled={isAnyActionPending}
          >
            {isDiscardPending ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        </SimpleTooltip>
      )}
    </div>
  );
}
